import clsx from 'clsx';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { ReactNode, useEffect, useRef } from 'react';

import BaseButton from '1_components/Buttons/BaseButton/BaseButton';
import Icon from '1_components/Icon/Icon';
import GoogleLogo from '1_components/SVGs/branding/googleLogo';
import SvgCaret from '1_components/SVGs/SvgCaret';
import Modal from '2_sections/ModalContainer/Modal/Modal';
import useAudioRoutingStore from 'state/audioRoutingStore';
import useClientSideRouting, {
  CLIENT_SIDE_PATH,
} from 'state/clientSideRoutingStore';
import { useDisableStore } from 'state/disableStore';
import { useHeaderStore } from 'state/headerStore';
import { useModalStore } from 'state/modalStore';
import { useAudioStore } from 'state/sfx/audioStore';
import useSoundsPreloadStore from 'state/soundsPreloadStore';
import useTilesStore from 'state/tilesStore';
import useWatchAgainStore from 'state/watchAgainStore';

import InfoMenuButton from './InfoMenuButton/InfoMenuButton';
import ReportButton from './ReportButton/ReportButton';

import S from './Header.module.scss';

import { usePathname } from 'hooks/navigation';
import { useRouter } from 'hooks/navigation';
import { fonts } from 'styles/fontsRegistry';

export interface HeaderProps {
  className?: string;
  sideMenu: ReactNode;
}

const Header = ({ sideMenu }: HeaderProps) => {
  const nextRouter = useRouter();
  const path = usePathname();
  const tiles_attributions = useTilesStore(s => s.attribution);
  const {
    isSideMenuOpen,
    setHeaderSettings,
    isHeaderVisible,
    isLogoVisible,
    isReportButtonVisible,
    backButtonDestination,
    isBackButtonVisible,
    headerOffset,
  } = useHeaderStore();
  const clientPath = useClientSideRouting(s => s.path);
  const isExperienceDisabled = useDisableStore(s => s.isExperienceDisabled);

  const setIsAudioPlaying = useAudioRoutingStore(s => s.setIsPlaying);
  const setModalComponent = useModalStore(s => s.setModalComponent);
  const isWatchAgain = useWatchAgainStore(s => s.isWatchAgain);

  const isAllowedAudio = useAudioStore(s => s.isAllowed);
  const buttonAudioRef = useRef<HTMLAudioElement | null>(null);
  const buttonAudioSrc = useSoundsPreloadStore(
    s => s.library?.['general_read_report_button']?.src
  );

  const ROUTES_WITH_GOOGLE_DISCLAIMER: Array<CLIENT_SIDE_PATH> = [
    'tiles',
    'weather',
    'landmark',
    'contractors',
  ];

  const ROUTES_WITH_GOOGLE_TILES_ATTRIBUTIONS: Array<CLIENT_SIDE_PATH> = [
    'tiles',
    'age-brackets',
    'peril_1',
    'peril_2',
    'contractors',
  ];

  const ROUTES_WITH_SUNCORP_LOGO: Array<CLIENT_SIDE_PATH> = [
    'age-brackets',
    'reels',
    'building_1',
    'building_2',
    'building_3',
    'peril_1',
    'peril_2',
    'outro',
  ];

  useEffect(() => {
    if (!isHeaderVisible) {
      setHeaderSettings({ isSideMenuOpen: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHeaderVisible]);

  const handleReportButtonClick = () => {
    setModalComponent(
      <Modal
        heading="Are you sure?"
        description="If you skip to your report, you'll miss hearing the rest of what your home has to say."
        leftButton={{
          label: 'No',
          onClick: () => {
            setIsAudioPlaying(true);
            setModalComponent(null);
          },
        }}
        rightButton={{
          label: 'Yes',
          onClick: () => {
            setModalComponent(null);
            nextRouter.push('/report');
          },
        }}
      />
    );
  };

  return (
    <motion.header
      className={S.header_wrapper}
      initial={{ opacity: 0, pointerEvents: 'none' }}
      animate={{
        translateY:
          path.includes('report') && !isSideMenuOpen
            ? Math.min(Math.max(0, headerOffset), 100) * -1
            : 0,
        opacity:
          path !== '/about-the-project' &&
          (path !== '/landing' || isAllowedAudio !== undefined) &&
          !isWatchAgain &&
          isHeaderVisible
            ? 1
            : 0,
        pointerEvents:
          path !== '/about-the-project' &&
          (path !== '/landing' || isAllowedAudio !== undefined) &&
          !isWatchAgain &&
          isHeaderVisible
            ? 'all'
            : 'none',
      }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.5,
        ease: 'easeInOut',
        translateY: { duration: 0, ease: 'linear' },
      }}
    >
      <motion.div
        className={clsx(
          S.logo_wrapper,
          path.includes('report')
            ? S.logo_wrapper_report
            : S.logo_wrapper_default
        )}
        initial={{ opacity: 0, pointerEvents: 'none' }}
        animate={{
          translateY: isSideMenuOpen
            ? Math.min(Math.max(0, headerOffset), 100) * -1
            : 0,
          opacity:
            ROUTES_WITH_SUNCORP_LOGO.includes(clientPath) ||
            isSideMenuOpen ||
            isLogoVisible
              ? 1
              : 0,
          pointerEvents:
            ROUTES_WITH_SUNCORP_LOGO.includes(clientPath) ||
            isLogoVisible ||
            isSideMenuOpen
              ? 'all'
              : 'none',
        }}
        transition={{
          duration: 0.3,
          ease: 'easeInOut',
          opacity: { duration: 0.3, ease: 'easeInOut', delay: 0.15 },
          pointerEvents: { duration: 0.3, ease: 'easeInOut', delay: 0.15 },
          translateY: { duration: 0, ease: 'linear', delay: 0 },
        }}
      >
        <Link href="https://suncorp.com.au/" target="_blank">
          <Icon variant="Logotype" />
        </Link>
      </motion.div>

      {path === '/experience' && !isExperienceDisabled && (
        <motion.div
          className={S.google_attribution_logo}
          initial={{ opacity: 0, pointerEvents: 'none' }}
          animate={{
            opacity:
              !isSideMenuOpen &&
              ROUTES_WITH_GOOGLE_DISCLAIMER.includes(clientPath)
                ? 1
                : 0,
            transition: { duration: 0.3, ease: 'easeInOut', delay: 0.15 },
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <GoogleLogo />
        </motion.div>
      )}

      {clientPath !== 'experience-preload' && !isExperienceDisabled && (
        <motion.div
          className={S.attributions}
          initial={{ opacity: 0, pointerEvents: 'none' }}
          animate={{
            opacity:
              !isSideMenuOpen &&
              ROUTES_WITH_GOOGLE_TILES_ATTRIBUTIONS.includes(clientPath)
                ? 1
                : 0,
            transition: { duration: 0.3, ease: 'easeInOut', delay: 0.15 },
          }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          {tiles_attributions?.map((attribution, index) => (
            <span key={index} className={fonts.support} style={{ fontSize: 7 }}>
              {attribution}
            </span>
          ))}
        </motion.div>
      )}

      <div
        className={clsx(
          S.info_wrapper,
          path.includes('report')
            ? S.info_wrapper_report
            : S.info_wrapper_default
        )}
      >
        {path === '/experience' &&
          isReportButtonVisible &&
          !isSideMenuOpen &&
          !isExperienceDisabled && (
            <>
              <ReportButton
                onClick={() => {
                  buttonAudioRef?.current?.play?.();
                  setIsAudioPlaying(false);
                  handleReportButtonClick();
                }}
              />

              <audio
                ref={buttonAudioRef}
                src={buttonAudioSrc}
                autoPlay={false}
                muted={!isAllowedAudio}
                preload="auto"
              />
            </>
          )}

        {isBackButtonVisible && !isSideMenuOpen && (
          <BaseButton
            onClick={() => nextRouter.push(backButtonDestination)}
            className={S.BackButton}
          >
            <SvgCaret /> Back
          </BaseButton>
        )}

        <InfoMenuButton />
      </div>

      <div
        className={S.menu}
        data-visible={(isSideMenuOpen && isHeaderVisible).toString()}
      >
        {sideMenu}
      </div>
    </motion.header>
  );
};
export default Header;
